import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  BusinessMetricsInput,
  calculateFixedCosts,
  processVisitsAndCalculateStats,
  calculateBreakEvenMetrics,
  calculatePriceIncreaseMetrics,
  calculateProfitPerHourMetrics,
  calculateVariableCostsMetrics,
  calculateInefficiencyAnalysis,
  calculateLoyaltyPointsReport,
  calculateServiceDetailedMetrics,
  BusinessMetrics,
} from './entity';

export const useBusinessMetrics = ({
  visits,
  overheadCosts,
  dateRange,
}: BusinessMetricsInput): BusinessMetrics => {
  const metrics = useMemo(() => {
    const filteredVisits = visits.filter(({ visit }) => {
      const visitDate = dayjs(visit.dateFrom);
      const fromDate = dayjs(dateRange.from);
      const toDate = dayjs(dateRange.to);

      return visitDate >= fromDate && visitDate <= toDate;
    });
    const fixedCosts = calculateFixedCosts(overheadCosts);

    const {
      serviceStats,
      totalVariableCosts,
      totalRevenue,
      totalPointsEarned,
      totalPointsRedeemed,
    } = processVisitsAndCalculateStats(filteredVisits);

    const breakEven = calculateBreakEvenMetrics(fixedCosts, totalVariableCosts, totalRevenue);

    const priceIncrease = calculatePriceIncreaseMetrics(
      fixedCosts,
      totalRevenue,
      totalVariableCosts,
      serviceStats,
    );

    const { profitPerHourMetrics, profitPerHourExplicit } =
      calculateProfitPerHourMetrics(serviceStats);

    const variableCosts = calculateVariableCostsMetrics(serviceStats);

    const topProfitPerHour = profitPerHourMetrics[0]?.profitPerHour || 0;

    const inefficiencyAnalysis = calculateInefficiencyAnalysis(
      serviceStats,
      totalRevenue,
      totalVariableCosts,
      topProfitPerHour,
    );

    const loyaltyPoints = calculateLoyaltyPointsReport(
      totalPointsRedeemed,
      totalPointsEarned,
      totalRevenue,
      filteredVisits,
    );

    const serviceDetailedMetrics = calculateServiceDetailedMetrics(
      serviceStats,
      fixedCosts,
      totalRevenue,
      totalVariableCosts,
    );

    return {
      breakEven,
      priceIncrease,
      profitPerHour: profitPerHourMetrics,
      variableCosts,
      profitPerHourExplicit,
      inefficiencyAnalysis,
      loyaltyPoints,
      serviceDetailedMetrics,
    };
  }, [visits, overheadCosts, dateRange]);

  return metrics;
};
